import { useState } from 'react';
import { DatePicker, Modal, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { LangText } from '@/components';
import { preFixImgUrl } from '@/utils';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import aiBtnImg from '@/assets/svg/ai-btn.svg';
import daysIconImg from '@/assets/svg/days-icon.svg';
import peopleIconImg from '@/assets/svg/people-icon.svg';
import departureIconImg from '@/assets/svg/departure-icon.svg';
import arrowRightIconImg from '@/assets/svg/arrow-right.svg';
import 'swiper/css';

const HOME_BANNERS = [
  '8bee7931abf575b99f59241dc7f0890a.png',
  'db12fdc0253e6c012c5c89fbe86d0a5e.png',
  '5abd95611071e987a642fcebfed32386.png',
] as const;

const HeroSearchCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [departureDate, setDepartureDate] = useState<Dayjs | null>(null);
  const [days, setDays] = useState<number>();
  const [people, setPeople] = useState<number>();
  const [activePicker, setActivePicker] = useState<'date' | 'days' | 'people' | null>(null);

  const dayOptions = Array.from({ length: 30 }, (_, index) => {
    const value = index + 1;
    return {
      value,
      label: t('home.tripSearch.daysValue', { count: value }),
    };
  });

  const peopleOptions = Array.from({ length: 10 }, (_, index) => {
    const value = index + 1;
    return {
      value,
      label: t('home.tripSearch.peopleValue', { count: value }),
    };
  });

  const generateTrip = () => {
    const searchParams = new URLSearchParams();

    if (departureDate) {
      searchParams.set('departureDate', departureDate.format('YYYY-MM-DD'));
    }
    if (days) {
      searchParams.set('days', String(days));
    }
    if (people) {
      searchParams.set('people', String(people));
    }

    const query = searchParams.toString();
    navigate(`/ai-trip${query ? `?${query}` : ''}`);
  };

  return (
    <div className="inline-flex h-[82px] w-fit items-center rounded-full bg-white pl-[12px] pr-[14px]">
      <div className="flex items-center">
        <div
          className="relative mr-[50px] flex cursor-pointer items-center gap-[12px]"
          onClick={() => setActivePicker('date')}
        >
          <span className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F4]">
            <img src={departureIconImg} alt="" className="h-[32px] w-[32px]" />
          </span>
          <div className="flex flex-col whitespace-nowrap">
            <LangText lngKey="common.form.departureDate" className="text-[16px] font-bold leading-[22px] text-[#000]" />
            <div className="relative mt-[6px] h-[20px] w-[70px] cursor-pointer">
              {departureDate ? (
                <span className="block text-[14px] leading-[20px] text-[#555]">
                  {departureDate.format('YYYY-MM-DD')}
                </span>
              ) : (
                <LangText
                  lngKey="home.tripSearch.selectDate"
                  className="block text-[14px] leading-[20px] text-[#979797]"
                />
              )}
            </div>
          </div>
        </div>

        <div
          className="relative mr-[50px] flex cursor-pointer items-center gap-[12px] border-l border-[#EEEEEE]"
          onClick={() => setActivePicker('days')}
        >
          <span className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F4]">
            <img src={daysIconImg} alt="" className="h-[26px] w-[26px]" />
          </span>
          <div className="flex flex-col whitespace-nowrap">
            <LangText lngKey="common.form.days" className="text-[16px] font-bold leading-[22px] text-[#000]" />
            <div className="relative mt-[6px] h-[20px] w-[42px] cursor-pointer">
              {days ? (
                <span className="block text-[14px] leading-[20px] text-[#555]">
                  {t('home.tripSearch.daysValue', { count: days })}
                </span>
              ) : (
                <LangText
                  lngKey="common.form.pleaseSelect"
                  className="block text-[14px] leading-[20px] text-[#979797]"
                />
              )}
            </div>
          </div>
        </div>

        <div
          className="relative mr-[50px] flex cursor-pointer items-center gap-[12px] border-l border-[#EEEEEE]"
          onClick={() => setActivePicker('people')}
        >
          <span className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F4]">
            <img src={peopleIconImg} alt="" className="h-[26px] w-[26px]" />
          </span>
          <div className="flex flex-col whitespace-nowrap">
            <LangText lngKey="common.form.people" className="text-[16px] font-bold leading-[22px] text-[#000]" />
            <div className="relative mt-[6px] h-[20px] w-[70px] cursor-pointer">
              {people ? (
                <span className="block text-[14px] leading-[20px] text-[#555]">
                  {t('home.tripSearch.peopleValue', { count: people })}
                </span>
              ) : (
                <LangText
                  lngKey="home.tripSearch.selectPeople"
                  className="block text-[14px] leading-[20px] text-[#979797]"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={generateTrip}
        className="flex h-[52px] shrink-0 items-center rounded-full bg-primary pl-[20px] pr-[24px] text-[18px] font-bold text-white transition-opacity hover:opacity-90"
      >
        <img src={aiBtnImg} alt="" className="h-[16px] w-[24px] shrink-0 object-contain mb-[3px]" />
        <LangText lngKey="home.common.generateTrip" className="text-[18px] ml-[4px] mr-[8px]" />
        <img src={arrowRightIconImg} alt="" className="h-[16px] w-[16px] shrink-0 object-contain" />
      </button>

      <Modal
        open={activePicker !== null}
        onCancel={() => setActivePicker(null)}
        footer={null}
        centered
        destroyOnHidden
        width={activePicker === 'date' ? 440 : 520}
        title={activePicker ? (
          <LangText
            lngKey={
              activePicker === 'date'
                ? 'common.form.departureDate'
                : activePicker === 'days'
                  ? 'common.form.days'
                  : 'common.form.people'
            }
            className="text-[20px] font-bold text-[#000]"
          />
        ) : null}
      >
        {activePicker === 'date' ? (
          <DatePicker
            value={departureDate}
            disabledDate={(current) => current.isBefore(dayjs().startOf('day'))}
            onChange={(value) => {
              setDepartureDate(value);
              setActivePicker(null);
            }}
            format="YYYY-MM-DD"
            placeholder={t('home.tripSearch.selectDate')}
            allowClear={false}
            inputReadOnly
            className="home-trip-modal-control h-[44px] w-full"
          />
        ) : null}

        {activePicker === 'days' ? (
          <Select<number>
            value={days}
            onChange={(value) => {
              setDays(value);
              setActivePicker(null);
            }}
            options={dayOptions}
            placeholder={t('common.form.pleaseSelect')}
            classNames={{ popup: { root: 'home-trip-modal-dropdown' } }}
            styles={{ placeholder: { fontSize: 16, lineHeight: '24px' } }}
            className="home-trip-modal-control h-[44px] w-full"
          />
        ) : null}

        {activePicker === 'people' ? (
          <Select<number>
            value={people}
            onChange={(value) => {
              setPeople(value);
              setActivePicker(null);
            }}
            options={peopleOptions}
            placeholder={t('home.tripSearch.selectPeople')}
            classNames={{ popup: { root: 'home-trip-modal-dropdown' } }}
            styles={{ placeholder: { fontSize: 16, lineHeight: '24px' } }}
            className="home-trip-modal-control h-[44px] w-full"
          />
        ) : null}
      </Modal>
    </div>
  );
};

export const HomeBanner = () => {
  return (
    <section className="relative h-[438px] w-full overflow-hidden bg-[#FAFAFA]">
      <Swiper
        className="w-full h-full"
        autoplay={{
          delay: 4500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        loop
        modules={[Autoplay]}
      >
        {HOME_BANNERS.map((image) => (
          <SwiperSlide key={image}>
            <div className="relative w-full h-full">
              <img
                src={preFixImgUrl(image)}
                alt=""
                className="object-cover absolute inset-0 w-full h-full"
                draggable={false}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="absolute inset-0 z-20 mx-auto flex h-full w-[1200px] flex-col justify-center">
        <div className="mb-[36px]">
          <LangText lngKey="home.banner.titleLine1" className="block text-[40px] font-bold leading-[56px] text-white drop-shadow" />
          <LangText lngKey="home.banner.titleLine2" className="block text-[40px] font-bold leading-[56px] text-white drop-shadow" />
          <LangText lngKey="home.banner.subtitle" className="mt-[8px] block text-[24px] leading-[34px] text-white" />
        </div>
        <HeroSearchCard />
      </div>
    </section>
  );
};
